import type { ChartData } from '../services/user-dashboard.service';

/**
 * Tabular rows returned by the custom dashboard API (same sources as the large-view data table).
 */
export function extractChartTableRows(chartData: ChartData | null | undefined): any[] {
  if (!chartData) {
    return [];
  }

  let tableData: any[] = [];

  if (chartData.data?.table_data && Array.isArray(chartData.data.table_data)) {
    tableData = chartData.data.table_data;
  } else if (chartData.table_data && Array.isArray(chartData.table_data)) {
    tableData = chartData.table_data;
  } else if (chartData.data && typeof chartData.data === 'object') {
    if ((chartData.data as any).data?.table_data && Array.isArray((chartData.data as any).data.table_data)) {
      tableData = (chartData.data as any).data.table_data;
    }
  } else if (
    chartData.data &&
    Array.isArray(chartData.data) &&
    chartData.data.length > 0 &&
    typeof chartData.data[0] === 'object' &&
    chartData.data[0] !== null &&
    !('series' in chartData.data[0]) &&
    !('categories' in chartData.data[0])
  ) {
    tableData = chartData.data;
  }

  return tableData;
}

const NAME_COLUMN_KEYS = ['name', 'category', 'dimension'];
const VOLUME_COLUMN_KEYS = ['count', 'volume'];
const PERCENT_COLUMN_KEYS = ['percentage'];

/** Name, then volume, then percentage, then any remaining columns in their original order. */
export function orderChartTableColumns(keys: string[]): string[] {
  const hasName = keys.some((key) => NAME_COLUMN_KEYS.includes(key.toLowerCase()));
  if (!hasName) {
    return keys;
  }

  const remaining = new Map(keys.map((key) => [key.toLowerCase(), key]));
  const ordered: string[] = [];
  const takeFirst = (candidates: string[]) => {
    const match = candidates.find((candidate) => remaining.has(candidate));
    if (!match) {
      return;
    }
    ordered.push(remaining.get(match) as string);
    remaining.delete(match);
  };

  takeFirst(NAME_COLUMN_KEYS);
  takeFirst(VOLUME_COLUMN_KEYS);
  takeFirst(PERCENT_COLUMN_KEYS);
  keys.forEach((key) => {
    const lower = key.toLowerCase();
    if (remaining.has(lower)) {
      ordered.push(key);
      remaining.delete(lower);
    }
  });
  return ordered;
}

export function collectTableColumnKeys(rows: any[]): string[] {
  if (!rows?.length) {
    return [];
  }
  const columns = new Set<string>();
  rows.forEach((row: any) => {
    if (row && typeof row === 'object') {
      Object.keys(row).forEach((key) => columns.add(key));
    }
  });
  return orderChartTableColumns(Array.from(columns));
}

function escapeCsvField(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  const s = typeof value === 'object' ? JSON.stringify(value) : String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function buildCsvFromTableRows(rows: any[], columns: string[]): string {
  const headerLine = columns.map(escapeCsvField).join(',');
  const bodyLines = rows.map((row) => columns.map((col) => escapeCsvField(row?.[col])).join(','));
  return [headerLine, ...bodyLines].join('\r\n');
}

export function sanitizeChartExportBasename(title: string | undefined): string {
  return (title || 'chart').replace(/[^a-z0-9]/gi, '_').toLowerCase();
}

/**
 * Builds a CSV from API table rows and triggers a browser download. Returns true if a file was downloaded.
 */
export function downloadChartTableCsv(chartData: ChartData | null | undefined): boolean {
  const rows = extractChartTableRows(chartData);
  if (!rows.length) {
    return false;
  }
  const columns = collectTableColumnKeys(rows);
  const csv = buildCsvFromTableRows(rows, columns);
  const base = sanitizeChartExportBasename(chartData?.title);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${base}.csv`;
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
  return true;
}
